import { useEffect, useState } from "react";
import { Users, BookOpen, DollarSign, Flag, Clock, Coins } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { Loading } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Admin() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
  }, []);

  if (!stats) return <Loading />;

  const cards = [
    { icon: Users, label: "Users", value: stats.users },
    { icon: BookOpen, label: "Published stories", value: stats.stories },
    { icon: DollarSign, label: "Gross revenue", value: `$${stats.gross_revenue_usd}` },
    { icon: Coins, label: "Coins sold", value: stats.coins_sold },
    { icon: Clock, label: "Awaiting review", value: stats.pending_review },
    { icon: DollarSign, label: "Payouts pending", value: stats.payouts_pending },
    { icon: Flag, label: "Open reports", value: stats.open_reports },
    { icon: Users, label: "Creators", value: stats.creators },
  ];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 animate-fade-up">
      <h1 className="font-display text-4xl mb-8">Admin Console</h1>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
        {cards.map((c) => (
          <div key={c.label} className="border border-white/10 rounded-xl p-5 bg-card/50">
            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-[0.15em] mb-3"><c.icon size={14} strokeWidth={1.5} /> {c.label}</div>
            <p className="font-display text-3xl tabular-nums">{c.value}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="payouts">
        <TabsList className="bg-card/50 border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="payouts" data-testid="admin-tab-payouts">Payouts</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="packages" data-testid="admin-tab-packages">Coin Packages</TabsTrigger>
          <TabsTrigger value="config" data-testid="admin-tab-config">Config</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit">Audit Log</TabsTrigger>
        </TabsList>
        <TabsContent value="payouts" className="mt-6"><PayoutsAdmin /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersAdmin /></TabsContent>
        <TabsContent value="packages" className="mt-6"><PackagesAdmin /></TabsContent>
        <TabsContent value="config" className="mt-6"><ConfigAdmin /></TabsContent>
        <TabsContent value="audit" className="mt-6"><AuditAdmin /></TabsContent>
      </Tabs>
    </div>
  );
}

function PayoutsAdmin() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/payouts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const act = async (id, action) => {
    try { await api.post(`/admin/payouts/${id}/action`, { action, note: "" }); toast.success(`Payout ${action}d`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!rows) return <Loading />;
  const sc = { requested: "text-amber-400", paid: "text-emerald-400", rejected: "text-primary" };
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="admin-payouts">
      {rows.length === 0 ? <p className="text-muted-foreground p-6 text-sm">No payout requests.</p> : rows.map((p) => (
        <div key={p.id} className="flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0">
          <div className="flex-1">
            <p className="text-sm">{p.creator_name} · {p.coins} coins (${p.amount_usd})</p>
            <p className="text-xs text-muted-foreground">{p.creator_email} · KYC: {p.kyc_status} · {new Date(p.requested_at).toLocaleDateString()}</p>
          </div>
          {p.status === "requested" ? (
            <>
              <Button data-testid={`payout-approve-${p.id}`} size="sm" onClick={() => act(p.id, "approve")} className="bg-emerald-600 hover:bg-emerald-700">Approve</Button>
              <Button data-testid={`payout-reject-${p.id}`} size="sm" variant="outline" onClick={() => act(p.id, "reject")} className="border-primary/40 text-primary hover:bg-primary/10">Reject</Button>
            </>
          ) : <span className={`text-xs uppercase tracking-[0.15em] ${sc[p.status]}`}>{p.status}</span>}
        </div>
      ))}
    </div>
  );
}

function UsersAdmin() {
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const setRole = async (id, role) => { try { await api.put(`/admin/users/${id}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const suspend = async (id) => { try { const { data } = await api.put(`/admin/users/${id}/suspend`); toast.success(data.suspended ? "Suspended" : "Unsuspended"); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!rows) return <Loading />;
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="admin-users">
      {rows.map((u) => (
        <div key={u.id} className="flex items-center gap-4 px-5 py-3 border-b border-white/5 last:border-0">
          <div className="flex-1 min-w-0">
            <p className="text-sm truncate">{u.name} {u.suspended && <span className="text-primary text-xs">(suspended)</span>}</p>
            <p className="text-xs text-muted-foreground truncate">{u.email}</p>
          </div>
          <Select value={u.role} onValueChange={(v) => setRole(u.id, v)}>
            <SelectTrigger data-testid={`role-${u.id}`} className="w-36 bg-black/30 border-white/10 h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{["LISTENER", "CREATOR", "MODERATOR", "ADMIN"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid={`suspend-${u.id}`} size="sm" variant="outline" onClick={() => suspend(u.id)} className="border-white/10 hover:bg-white/5">{u.suspended ? "Unsuspend" : "Suspend"}</Button>
        </div>
      ))}
    </div>
  );
}

function PackagesAdmin() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState({ name: "", coins: 100, price_usd: 4.99, active: true });
  const load = () => api.get("/admin/packages").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => { try { await api.post("/admin/packages", form); toast.success("Package created"); setForm({ name: "", coins: 100, price_usd: 4.99, active: true }); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (p) => { try { await api.put(`/admin/packages/${p.id}`, { ...p, active: !p.active }); load(); } catch (e) { toast.error(apiError(e)); } };
  if (!rows) return <Loading />;
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="border border-white/10 rounded-xl overflow-hidden h-fit" data-testid="admin-packages">
        {rows.map((p) => (
          <div key={p.id} className="flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0">
            <div className="flex-1"><p className="text-sm">{p.name}</p><p className="text-xs text-gold">{p.coins} coins · ${p.price_usd}</p></div>
            <Button size="sm" variant="outline" onClick={() => toggle(p)} className={`border-white/10 ${p.active ? "text-emerald-400" : "text-muted-foreground"}`}>{p.active ? "Active" : "Inactive"}</Button>
          </div>
        ))}
      </div>
      <div className="border border-white/10 rounded-xl p-6 bg-card/40 h-fit">
        <h3 className="font-display text-xl mb-4">New package</h3>
        <div className="space-y-3">
          <Input data-testid="pkg-name" placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-white/10" />
          <Input data-testid="pkg-coins" type="number" placeholder="Coins" value={form.coins} onChange={(e) => setForm({ ...form, coins: parseInt(e.target.value) || 0 })} className="bg-black/30 border-white/10" />
          <Input data-testid="pkg-price" type="number" step="0.01" placeholder="Price USD" value={form.price_usd} onChange={(e) => setForm({ ...form, price_usd: parseFloat(e.target.value) || 0 })} className="bg-black/30 border-white/10" />
          <Button data-testid="pkg-create" onClick={create} disabled={!form.name} className="bg-primary hover:bg-primary/90 w-full">Create package</Button>
        </div>
      </div>
    </div>
  );
}

function ConfigAdmin() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/admin/config").then((r) => setCfg(r.data)); }, []);
  const save = async () => {
    try {
      await api.put("/admin/config", {
        commission_pct: parseInt(cfg.commission_pct), hold_days: parseInt(cfg.hold_days),
        min_payout_coins: parseInt(cfg.min_payout_coins), payout_coin_usd: parseFloat(cfg.payout_coin_usd),
      });
      toast.success("Config saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!cfg) return <Loading />;
  const fields = [
    { k: "commission_pct", label: "Commission %", step: "1" },
    { k: "hold_days", label: "Payout hold (days)", step: "1" },
    { k: "min_payout_coins", label: "Min payout (coins)", step: "1" },
    { k: "payout_coin_usd", label: "Payout rate ($/coin)", step: "0.001" },
  ];
  return (
    <div className="border border-white/10 rounded-xl p-6 bg-card/40 max-w-lg" data-testid="admin-config">
      <div className="grid grid-cols-2 gap-4">
        {fields.map((f) => (
          <div key={f.k}>
            <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{f.label}</Label>
            <Input data-testid={`config-${f.k}`} type="number" step={f.step} value={cfg[f.k]} onChange={(e) => setCfg({ ...cfg, [f.k]: e.target.value })} className="mt-1.5 bg-black/30 border-white/10" />
          </div>
        ))}
      </div>
      <Button data-testid="config-save" onClick={save} className="mt-5 bg-primary hover:bg-primary/90">Save config</Button>
    </div>
  );
}

function AuditAdmin() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit-logs").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loading />;
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden" data-testid="admin-audit">
      {rows.length === 0 ? <p className="text-muted-foreground p-6 text-sm">No audit entries yet.</p> : rows.map((l) => (
        <div key={l.id} className="px-5 py-3 border-b border-white/5 last:border-0 text-sm flex items-center justify-between">
          <span><span className="text-gold font-mono text-xs">{l.action}</span> by {l.actor_email}</span>
          <span className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
        </div>
      ))}
    </div>
  );
}
